import React, { useState, useEffect } from "react";

import {
  Grid,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert,
  Stack,
  Box,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Checkbox,
} from "@mui/material";

import EditRegularTemplate from "./EditRegularTemplate";
import MessageNode from "../MessageNode";
import EditCarouselTemplate from "./EditCarouselTemplate";
import MultipleProductsTemplateEditor from "../MultipleProductsTemplateEditor";
import SingleProductTemplateEditor from "../SingleProductTemplateEditor";

import axios from "axios";

import { TEMPLATE_EDIT_ENDPOINT } from "../../../config/urls";
import { useAuth } from "../../../hooks/AuthProvider";
import { ContinuousColorLegend } from "@mui/x-charts";
import ButtonOne from "../../../common/normal-button";
import { useUnsavedChanges, clearUnsavedChanges } from "../../../common/unsaved-changes";

const MPM_PRODUCT_LIMIT = 30;

const EditTemplate = (props) => {
  const { getValidToken } = useAuth();
  const { templatesNamesData, setComponent, templatesData } = props;
  const trackChanges = useUnsavedChanges();

  const [loading, setLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);

  const [headerDuplicatePlaceholderError, setHeaderDuplicatePlaceholderError] =
    useState(false);
  const [bodyDuplicatePlaceholderError, setBodyDuplicatePlaceholderError] =
    useState(false);

  const [
    carouselMainBodyDuplicatePlaceholderError,
    setCarouselMainBodyDuplicatePlaceholderError,
  ] = useState(false);
  const [
    carouselCardBodyDuplicatePlaceholderError,
    setCarouselCardBodyDuplicatePlaceholderError,
  ] = useState([]);
  // State
  const [headerTextError, setHeaderTextError] = useState("");
  const [templateTracking, setTemplateTracking] = useState(templatesData?.template_tracking || false);

  // Extract preview data if available
  const preview = templatesData?.template_preview_json || {};
  const req_category = templatesData?.category || "";
  
  const category_mapping = {
    "MARKETING": "marketing-template",
    "UTILITY": "utility-template",
  };

  const category = category_mapping[req_category] || "marketing-template";

  console.log("category: ", category);

  console.log("preview: ", preview);

  // Helper to handle empty string or undefined as []
  const parseArray = (val) => {
    if (!val || val === "") return [];
    try {
      return typeof val === "string" ? JSON.parse(val) : val;
    } catch {
      return [];
    }
  };

  // Helper to parse the saved MPM thumbnail (stringified {title, retailerId, imageUrl})
  // back into the {name, retailer_id, image_url} shape the product Autocomplete expects
  const parseMpmThumbnail = (val) => {
    if (!val) return null;
    let parsed;
    try {
      parsed = typeof val === "string" ? JSON.parse(val) : val;
    } catch {
      return null;
    }
    if (!parsed || !parsed.retailerId) return null;
    return {
      name: parsed.title,
      retailer_id: parsed.retailerId,
      image_url: parsed.imageUrl,
    };
  };

  // Helper to parse carousel cards from preview (with cardFile URLs)
  const parseCarouselCards = (preview) => {
    let cards = [];
    if (preview.carouselCards) {
      try {
        cards =
          typeof preview.carouselCards === "string"
            ? JSON.parse(preview.carouselCards)
            : preview.carouselCards;
      } catch {
        cards = [];
      }
    }
    // Attach cardFile URLs if present in preview
    cards = cards.map((card, idx) => {
      const fileKey = `cardFile_${idx}`;
      let headerFile = null;
      if (preview[fileKey]) {
        // Fetch the file from the URL and create a File object for UI usage
        // We'll use a Blob with the URL as a placeholder, but allow user to replace it in UI
        // The MessageNode and EditCarouselTemplate should handle both File and URL string
        headerFile = preview[fileKey];
        // Optionally, you can fetch and convert to File/Blob here if you want to strictly use File objects
        // But for preview, passing the URL string is enough for most React UIs
      }
      return {
        ...card,
        headerFile: headerFile,
      };
    });
    return cards.length > 0
      ? cards
      : [
          {
            id: Date.now(),
            headerType: (preview.carouselType || "image").toUpperCase(),
            headerFile: null,
            body: "",
            sampleBodyFields: [],
            buttons: [
              {
                type: "QUICK_REPLY",
                data: {
                  buttonText: "",
                },
              },
            ],
          },
        ];
  };

  // State initializations with autofill logic
  const [templateOps, setTemplateOps] = useState("edit-template");
  const [templateType, setTemplateType] = useState(category || "");
  const [marketingOption, setMarketingOption] = useState(
    preview.marketingOption || ""
  );
  const [utilityOption, setUtilityOption] = useState("custom");
  const templateSteps = ["setup-template", "edit-template"];
  console.log(templateType, "props in edit template")

  const [carouselType, setCarouselType] = useState(
    preview.carouselType ||
      (preview.marketingOption === "carousel"
        ? parseCarouselCards(preview)[0]?.headerType?.toLowerCase() || "image"
        : "image")
  );
  const [carouselMainBodySampleFields, setCarouselMainBodySampleFields] =
    useState(parseArray(preview.carouselMainBodySampleFields));

  const columns = [
    { id: "header", label: "Header" },
    { id: "body", label: "Body" },
    { id: "footer", label: "Footer" },
  ];

  const [templateName, setTemplateName] = useState(
    preview.templateName || templatesData?.template_name || ""
  );
  const [language, setLanguage] = useState(
    preview.language || templatesData?.language || "english"
  );
  const [headerType, setHeaderType] = useState(preview.headerType || "none");
  const [headerText, setHeaderText] = useState(preview.headerText || "");
  const [headerFile, setHeaderFile] = useState(preview.headerFile || null);
  const [body, setBody] = useState(preview.body || "");
  const [sampleBodyFields, setsampleBodyFields] = useState(
    parseArray(preview.sampleBodyFields)
  );
  const [sampleHeaderFields, setsampleHeaderFields] = useState(
    parseArray(preview.sampleHeaderFields)
  );
  const [footer, setFooter] = useState(preview.footer || "");
  const [allButtons, setAllButtons] = useState(parseArray(preview.allButtons));
  const [isButtonsValid, setIsButtonsValid] = useState(true);
  const [error, setError] = useState(false);

  const _location = preview?.location
    ? typeof preview.location === "string"
      ? JSON.parse(preview.location)
      : preview.location
    : {};
  const [location, setLocation] = useState(_location);
  const [locationLatitudeError, setLocationLatitudeError] = useState(false);
  const [locationLongitudeError, setLocationLongitudeError] = useState(false);

  // Multiple products template state
  const createMpmSection = (id) => ({
    id,
    title: `Section ${id}`,
    products: [],
  });

  const createMpmProduct = (id) => ({
    id,
    title: `Product ${id}`,
    value: "",
    retailerId: "",
  });

  const [mpmSections, setMpmSections] = useState(
    preview.marketingOption === "multiple-products"
      ? parseArray(preview.mpmSections)
      : [createMpmSection(1)]
  );
  const [mpmThumbnail, setMpmThumbnail] = useState(
    parseMpmThumbnail(preview.mpmThumbnail)
  );
  // spmProduct is saved in the same {title, retailerId, imageUrl} shape as the MPM thumbnail
  const [spmProduct, setSpmProduct] = useState(
    parseMpmThumbnail(preview.spmProduct)
  );

  const [carouselMainBody, setCarouselMainBody] = useState(
    preview.carouselMainBody || ""
  );
  const [carouselCards, setCarouselCards] = useState(
    preview.marketingOption === "carousel"
      ? parseCarouselCards(preview)
      : [
          {
            id: Date.now(),
            headerType: (preview.carouselType || "image").toUpperCase(),
            headerFile: null,
            body: "",
            sampleBodyFields: [],
            buttons: [
              {
                type: "QUICK_REPLY",
                data: {
                  buttonText: "",
                },
              },
            ],
          },
        ]
  );

  const [formData, setFormData] = useState({
    templateName: "",
    language: "",
    headerType: "",
    headerText: "",
    headerFile: "",
    sampleHeaderFields: [],
    body: "",
    sampleBodyFields: [],
    footer: "",
    marketingOption: "",
    templateType: "",
    allButtons: [],
    carouselMainBodySampleFields: [],
    templateTracking: false,
    mpmSections: [],
    mpmThumbnail: null,
    spmProduct: null
  });

  const [alertObj, setAlertObj] = useState({
    active: false,
    msg: "",
    type: "",
    statusCode: "",
  });

      const endsWithParameter = (text) => {
    if (!text) return false;

    const s = typeof text === "string" ? text : String(text);

    // Last non-empty line
    const lines = s.split(/\r?\n/).filter(line => line.trim() !== "");
    if (lines.length === 0) return false;

    const lastLine = lines[lines.length - 1];

    // Rule: invalid if last line ends with {{number}} followed only by whitespace/punctuation
    return /\{\{\d+\}\}[\s\p{P}]*$/u.test(lastLine);
  };

  const handleSuccessAlert = () => {
    setAlertObj({
      active: true,
      type: "success",
      msg: "Template updated successfully",
      statusCode: 201,
    });
    clearUnsavedChanges();
    // Wait 2 second before navigating away so the alert shows
    setTimeout(() => {
      setComponent(1);
    }, 2000);
  };

  const handleErrorAlert = (statusCode, responseText) => {
    setAlertObj({
      active: true,
      type: "error",
      msg: `Failed to submit template: ${responseText}`,
      statusCode: statusCode,
    });
  };

  const handleAlertClose = () => {
    setAlertObj({
      active: false,
      type: "",
      msg: "",
      statusCode: "",
    });
  };

  useEffect(() => {
    const nextFormData = {
      templateName,
      language,
      headerType,
      headerText,
      headerFile,
      sampleHeaderFields,
      body,
      sampleBodyFields,
      footer,
      marketingOption,
      templateType,
      allButtons,
      carouselCards,
      carouselMainBody,
      carouselMainBodySampleFields,
      carouselType,
      location,
      templateTracking,
      mpmSections,
      mpmThumbnail,
      spmProduct
    };
    setFormData(nextFormData);
    // First run holds the loaded template; any later difference is an unsaved edit
    trackChanges(nextFormData);
  }, [
    templateName,
    language,
    headerType,
    headerText,
    headerFile,
    sampleHeaderFields,
    body,
    sampleBodyFields,
    footer,
    marketingOption,
    templateType,
    allButtons,
    carouselCards,
    carouselMainBody,
    carouselMainBodySampleFields,
    carouselType,
    location,
    templateTracking,
    mpmSections,
    mpmThumbnail,
    spmProduct
  ]);

  const handleSubmitData = async (event) => {
    const apiUrl = TEMPLATE_EDIT_ENDPOINT;

    if (
      headerTextError || // NEW check
      headerDuplicatePlaceholderError ||
      bodyDuplicatePlaceholderError ||
      carouselMainBodyDuplicatePlaceholderError ||
      carouselCardBodyDuplicatePlaceholderError.some(Boolean)
    ) {
      setAlertObj({
        active: true,
        type: "error",
        msg:
          headerTextError || "Please resolve the parameter errors in the form",
      });
      return;
    }

     if (
        endsWithParameter(formData.body) ||
        endsWithParameter(formData.headerText) ||
        endsWithParameter(formData.footer) ||
        endsWithParameter(formData.carouselMainBodySampleFields)
      ) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "A template cannot end with a parameter (e.g., '{{1}}'). Please add text after it.",
        });
        return;
      }

    // If carousel, validate each card
    if (formData.marketingOption === "carousel" && Array.isArray(formData.carouselCards)) {
      for (let i = 0; i < formData.carouselCards.length; i++) {
        if (endsWithParameter(formData.carouselCards[i].body)) {
          setAlertObj({
            active: true,
            type: "error",
            msg: `Carousel Card ${i + 1} body cannot end with a parameter e.g., '{{1}}'. Please add text after it.`,
          });
          return;
        }
      }
    }

    // Prevent submit if there are duplicate placeholder errors
    if (
      headerDuplicatePlaceholderError ||
      bodyDuplicatePlaceholderError ||
      carouselMainBodyDuplicatePlaceholderError ||
      carouselCardBodyDuplicatePlaceholderError.some(Boolean)
    ) {
      setAlertObj({
        active: true,
        type: "error",
        msg: "Please resolve the parameter errors in the form",
      });
      return;
    }

    // If it's a carousel template, check if each card has a required media and body
    if (formData.marketingOption === "carousel") {
      if (formData.carouselCards.length === 0) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Please add at least one card",
        });
        return;
      }

      // Check if main body is empty
      if (
        !formData.carouselMainBody ||
        formData.carouselMainBody.trim() === ""
      ) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Please enter a main message body for the template",
        });
        return;
      }

      // Validate each card
      for (let i = 0; i < formData.carouselCards.length; i++) {
        const card = formData.carouselCards[i];
        if (!card.headerFile) {
          setAlertObj({
            active: true,
            type: "error",
            msg: `Please add ${
              formData.carouselType === "video" ? "a video" : "an image"
            } for Card ${i + 1}`,
          });
          return;
        }

        // For video files, add additional validation if needed
        if (formData.carouselType === "video" && card.headerFile) {
          // Optional: Add video-specific validation (e.g., file size, duration)
          const fileSize = card.headerFile.size / (1024 * 1024); // Convert to MB
          if (fileSize > 16) {
            // WhatsApp typically limits videos to 16MB
            setAlertObj({
              active: true,
              type: "error",
              msg: `Video file for Card ${
                i + 1
              } exceeds the maximum size of 16MB`,
            });
            return;
          }
        }

        if (!card.body || card.body.trim() === "") {
          setAlertObj({
            active: true,
            type: "error",
            msg: `Please add body text for Card ${i + 1}`,
          });
          return;
        }
        if (card.buttons.length === 0) {
          setAlertObj({
            active: true,
            type: "error",
            msg: `Please add at least one button for Card ${i + 1}`,
          });
          return;
        }
      }
    } else if (formData.marketingOption === "single-product") {
      // SINGLE PRODUCT TEMPLATE SPECIFIC VALIDATION
      if (!formData.body?.trim()) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Message body is required",
        });
        document.getElementById("template-body")?.focus();
        return;
      }

      if (!formData?.spmProduct || !formData?.spmProduct?.retailer_id) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Please select a product",
        });
        return;
      }
    } else if (formData.marketingOption === "multiple-products") {
      // MULTIPLE PRODUCTS TEMPLATE SPECIFIC VALIDATION
      if (!formData.body?.trim()) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Message body is required",
        });
        document.getElementById("template-body")?.focus();
        return;
      }

      if (formData?.mpmSections?.length === 0) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Please add at least one section",
        });
        return;
      }

      const totalProducts = formData?.mpmSections?.reduce(
        (total, section) => total + section?.products?.length,
        0
      );

      if (totalProducts < 1 || totalProducts > MPM_PRODUCT_LIMIT) {
        setAlertObj({
          active: true,
          type: "error",
          msg: `Total number of products must be between 1 and ${MPM_PRODUCT_LIMIT}`,
        });
        return;
      }

      if (!formData?.mpmThumbnail || !formData?.mpmThumbnail?.retailer_id) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Please select a thumbnail product",
        });
        return;
      }

      // Check for duplicate products
      const productIds = new Set();
      for (const section of formData.mpmSections) {
        for (const product of section.products) {
          if (productIds.has(product.retailerId)) {
            setAlertObj({
              active: true,
              type: "error",
              msg: "Duplicate product found in multiple sections",
            });
            return;
          }
          productIds.add(product.retailerId);
        }
      }
    } else {
      // Original validation for non-carousel templates
      if (
        formData.headerType === "image" ||
        formData.headerType === "video" ||
        formData.headerType === "document"
      ) {
        if (!formData.headerFile) {
          setAlertObj({
            active: true,
            type: "error",
            msg: "Please upload a media file",
          });
          return;
        }
      } else if (formData.headerType === "location") {
        if (!location) {
          setAlertObj({
            active: true,
            type: "error",
            msg: "Please enter a location",
          });
          return;
        }
      }
      if (!formData.body?.trim()) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Message body is required",
        });
        document.getElementById("template-body")?.focus();
        return;
      }
    }

    const data = { ...formData };
    data.sampleHeaderFields =
      data.sampleHeaderFields.length === 0
        ? ""
        : JSON.stringify(data.sampleHeaderFields);
    data.sampleBodyFields =
      data.sampleBodyFields.length === 0
        ? ""
        : JSON.stringify(data.sampleBodyFields);
    data.allButtons =
      data.allButtons.length === 0 ? "" : JSON.stringify(data.allButtons);

    // process carousel main body sample fields
    data.carouselMainBodySampleFields =
      data.carouselMainBodySampleFields?.length === 0
        ? ""
        : JSON.stringify(data.carouselMainBodySampleFields);

    data.location =
      location && Object.keys(location).length > 0
        ? JSON.stringify(location)
        : "";

    data.templateTracking =
      (data.allButtons && data.allButtons.length > 0) ? data.templateTracking : false;

    // process mpmSections
    data.mpmSections =
      data.mpmSections?.length === 0 ? "" : JSON.stringify(data.mpmSections);

    // process mpmThumbnail
    data.mpmThumbnail = data.mpmThumbnail
      ? JSON.stringify({
          title: data.mpmThumbnail.name,
          retailerId: data.mpmThumbnail.retailer_id,
          imageUrl: data.mpmThumbnail.image_url,
        })
      : "";

    // process spmProduct
    data.spmProduct = data.spmProduct
      ? JSON.stringify({
          title: data.spmProduct.name,
          retailerId: data.spmProduct.retailer_id,
          imageUrl: data.spmProduct.image_url,
        })
      : "";

    // Raise error if body parameter is present but is empty
    if (sampleBodyFields) {
      const invalidFieldIndex = sampleBodyFields.findIndex((field) => {
        if (!field || !field.type || !field.hasOwnProperty('value')) {
          return true;
        }
        
        const trimmedValue = typeof field.value === 'string' ? field.value.trim() : field.value;
        
        if (trimmedValue === "" || trimmedValue === null || trimmedValue === undefined) {
          return true;
        }
        
        if (field.type === "custom" && trimmedValue === "") {
          return true;
        }
        
        return false;
      });

      if (invalidFieldIndex !== -1) {
        setAlertObj({
          active: true,
          type: "error",
          msg: `Please provide a value for body variable {{${invalidFieldIndex + 1}}} before submitting.`,
        });
        return;
      }
    }

    console.log("Payload: ", data);

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    // Use FormData to handle file uploads properly
    const formDataObj = new FormData();

    // Add all regular fields to FormData
    for (const key in data) {
      if (key !== "headerFile" && key !== "carouselCards") {
        formDataObj.append(key, data[key]);
      }
    }

    // Handle carousel cards specially
    if (data.marketingOption === "carousel") {
      // First, create a simplified version of cards without the file objects
      const simplifiedCards = data.carouselCards.map((card) => {
        const { headerFile, ...rest } = card;
        return rest;
      });

      formDataObj.append("carouselCards", JSON.stringify(simplifiedCards));

      // Then append each card's file separately
      for (let index = 0; index < data.carouselCards.length; index++) {
        const card = data.carouselCards[index];
        if (card.headerFile) {
          // If headerFile is a string (URL), convert it to a File object before appending
          if (typeof card.headerFile === "string") {
            const url = card.headerFile;
            const filename = url.split("/").pop() || `cardFile_${index}`;

            // Dynamically determine file type
            let fileType = "image/jpeg";
            if (carouselType === "video" || url.endsWith(".mp4")) {
              fileType = "video/mp4";
            } else if (url.endsWith(".png")) {
              fileType = "image/png";
            } else if (url.endsWith(".jpg") || url.endsWith(".jpeg")) {
              fileType = "image/jpeg";
            }

            // Fetch the file content from the URL
            try {
              const headers = {
                "X-Tenant-Id": tenant,
              };

              const response = await fetch(url, { headers });
              const blob = await response.blob();
              const fileObj = new File([blob], filename, { type: fileType });

              console.log(
                `Fetched file size for cardFile_${index}:`,
                fileObj.size
              );

              formDataObj.append(`cardFile_${index}`, fileObj);
            } catch (err) {
              console.error(`Failed to fetch file for cardFile_${index}:`, err);
              // Fallback: append an empty file if fetch fails
              formDataObj.append(
                `cardFile_${index}`,
                new File([], filename, { type: fileType })
              );
            }
          } else {
            formDataObj.append(`cardFile_${index}`, card.headerFile);
          }
        }
      }
    } else if (data.headerFile) {
      // Handle regular template file
      formDataObj.append("headerFile", data.headerFile);
    }

    // Log the FormData entries for debugging
    for (let pair of formDataObj.entries()) {
      if (pair[0].startsWith("cardFile_") || pair[0] === "headerFile") {
        console.log(`${pair[0]}: File object with name ${pair[1].name}`);
      } else {
        console.log(
          `${pair[0]}: ${
            typeof pair[1] === "string" && pair[1].length > 100
              ? pair[1].substring(0, 100) + "..."
              : pair[1]
          }`
        );
      }
    }

    setLoading(true);

    axios
      .put(apiUrl, formDataObj, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
          "X-Tenant-Id": tenant,
        },
      })
      .then((response) => {
        console.log("API Response:", response.data);
        // axios only resolves on 2xx (update returns 200), so trust the body's status
        if (response.data?.status !== "error") {
          handleSuccessAlert();
          // Disable submit button after successful submission
          setSubmitDisabled(true);
        } else {
          let message = response.data.message
            ? response.data.message
            : response.statusText;
          handleErrorAlert(response.status, message);
        }
      })
      .catch((error) => {
        console.error("API Error:", error);
        setAlertObj({
          active: true,
          type: "error",
          msg: `Failed to submit form: ${
            error.response?.data?.message || error.response?.statusText || error.message
          }`,
          statusCode: error.response?.status || 500,
        });
      })
      .finally(() => {
        setLoading(false); // This will run whether the request succeeds or fails
      });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const currentIndex = templateSteps.indexOf(templateOps);
    if (!isButtonsValid || error) {
      return;
    }
    if (currentIndex === templateSteps.length - 1) {
      // Submit the form data here
      setFormData({
        templateName,
        language,
        headerType,
        headerText,
        headerFile,
        sampleHeaderFields,
        body,
        sampleBodyFields,
        footer,
        marketingOption,
        templateType,
        allButtons,
        carouselCards,
        carouselMainBody,
        carouselMainBodySampleFields,
        location,
        templateTracking,
        mpmSections,
        mpmThumbnail,
        spmProduct
      });
      handleSubmitData();
      console.log("Called the API");
    } else {
      setTemplateOps(templateSteps[currentIndex + 1]);
    }
  };

  const handleMarketingOptionChange = (event) => {
    setMarketingOption(event.target.value);
    setUtilityOption("");
  };

  const handleUtilityOptionChange = (event) => {
    setUtilityOption(event.target.value);
    setMarketingOption("");
  };

  const handlePrevious = () => {
    const currentIndex = templateSteps.indexOf(templateOps);
    if (currentIndex > 0) {
      setTemplateOps(templateSteps[currentIndex - 1]);
    }
  };

  useEffect(() => {
    let timeoutId;

    if (alertObj.active) {
      timeoutId = setTimeout(() => {
        handleAlertClose();
      }, 5000);
    }

    // Cleanup function to clear the timeout when component unmounts or when alertObj changes
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [alertObj]);

  useEffect(() => {
    if (carouselCards?.length > 0 && formData.marketingOption === "carousel") {
      const headerTypeValue = carouselType === "video" ? "VIDEO" : "IMAGE";

      const hasIncorrectTypes = carouselCards.some(
        (card) => card.headerType !== headerTypeValue
      );

      if (hasIncorrectTypes) {
        const updatedCards = carouselCards.map((card) => ({
          ...card,
          headerType: headerTypeValue,
        }));

        setCarouselCards(updatedCards);
      }
    }
  }, [carouselType, carouselCards]);

  return (
    <div sx={{ padding: 2 }}>
      <form
        onSubmit={handleSubmit}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 2,
          mt: 3
        }}
      >
        <Stack spacing={3} sx={{ mt: { xs: 0, md: -6 } }}>
          {/* Submit Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <ButtonOne variant="contained" type="submit" disabled={loading || submitDisabled}>
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} /> Submitting...
                </>
              ) : (
                <>
                  Submit for Approval
                </>
              )}
            </ButtonOne>
          </Box>

          {/* Alert */}
          {alertObj.active && (
            <Grid item xs={12}>
              <Alert severity={alertObj.type} onClose={handleAlertClose}>
                {alertObj.msg}
              </Alert>
            </Grid>
          )}

          {/* Main Stack */}
          <Stack spacing={3} sx={{ width: "100%" }}>
            {/* Sub Stack */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              sx={{
                width: "100%",
                flexWrap: { xs: "wrap", sm: "nowrap" },
                gap: 2,
              }}
            >
              {/* Template Name */}
              <Tooltip title="Template name" arrow placement="top">
                <TextField
                  sx={{ flex: 1, minWidth: { xs: "100%", sm: "auto" } }}
                  label="Template Name"
                  value={templateName}
                  InputProps={{
                    inputProps: {
                      maxLength: 512,
                    },
                  }}
                  required
                  disabled
                />
              </Tooltip>

              {/* Category */}
              <FormControl
                sx={{ flex: 1, minWidth: { xs: "100%", sm: "auto" } }}
                disabled
              >
                <InputLabel id="template-type-label">Category</InputLabel>
                <Select
                  labelId="template-type-label"
                  id="template-type"
                  name="template-type"
                  value={category}
                  label="Category"
                  onChange={(event) => setTemplateType(event.target.value)}
                  defaultValue="marketing-template"
                >
                  <MenuItem value="marketing-template">Marketing</MenuItem>
                  <MenuItem value="utility-template">Utility</MenuItem>
                </Select>
              </FormControl>

              {/* Type */}
              <FormControl
                sx={{ flex: 1, minWidth: { xs: "100%", sm: "auto" } }}
              >
                <InputLabel id="marketing-option-label">Type</InputLabel>
                <Select
                  labelId="type-label"
                  id="type"
                  name="type"
                  value={
                    templateType === "marketing-template"
                      ? marketingOption
                      : utilityOption
                  }
                  label="Type"
                  onChange={
                    templateType === "marketing-template"
                      ? handleMarketingOptionChange
                      : handleUtilityOptionChange
                  }
                >
                  <MenuItem value="custom">Custom</MenuItem>
                  {templateType === "marketing-template" && (
                    <MenuItem value="carousel">Carousel</MenuItem>
                  )}
                  {templateType === "marketing-template" && (
                    <MenuItem value="single-product">Single Product</MenuItem>
                  )}
                  {templateType === "marketing-template" && (
                    <MenuItem value="multiple-products">
                      Multiple Products
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              {/* Language */}
              <Tooltip title="Language" arrow placement="top">
                <FormControl
                  sx={{ flex: 1, minWidth: { xs: "100%", sm: "auto" } }}
                  disabled
                >
                  <InputLabel id="marketing-option-label">Language</InputLabel>
                  <Select
                    label="Language"
                    value={language}
                    onChange={(event) => setLanguage(event.target.value)}
                    required
                  >
                    <MenuItem value="english">English</MenuItem>
                    <MenuItem value="spanish">Spanish</MenuItem>
                    <MenuItem value="hindi">Hindi</MenuItem>
                    <MenuItem value="marathi">Marathi</MenuItem>
                    <MenuItem value="malayalam">Malayalam</MenuItem>
                    <MenuItem value="tamil">Tamil</MenuItem>
                    <MenuItem value="bengali">Bengali</MenuItem>
                    <MenuItem value="telugu">Telugu</MenuItem>
                    <MenuItem value="gujarati">Gujarati</MenuItem>
                    <MenuItem value="kannada">Kannada</MenuItem>
                  </Select>
                </FormControl>
              </Tooltip>
            </Stack>
          </Stack>

          <Divider />

          {/* Custom Template */}
          {((templateType === "marketing-template" &&
            marketingOption === "custom") ||
            (templateType === "utility-template" &&
              utilityOption === "custom")) && (
            <Stack
              direction={{ xs: "column", md: "row" }}
              sx={{ flexDirection: { xs: "column", md: "row" } }}
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <EditRegularTemplate
                  templateType={templateType}
                  templateName={templateName}
                  setTemplateName={setTemplateName}
                  language={language}
                  setLanguage={setLanguage}
                  headerType={headerType}
                  setHeaderType={setHeaderType}
                  headerText={headerText}
                  setHeaderText={setHeaderText}
                  headerFile={
                    typeof headerFile === "string" && headerFile
                      ? (() => {
                          console.log("headerFile: ", headerFile);
                          const url = headerFile;

                          const filename = url.split("/").pop() || "headerFile";
                          console.log("filename: ", filename);
                          let fileType = "image/jpeg";
                          if (headerType === "video" || url.endsWith(".mp4")) {
                            fileType = "video/mp4";
                          } else if (url.endsWith(".png")) {
                            fileType = "image/png";
                          } else if (
                            url.endsWith(".jpg") ||
                            url.endsWith(".jpeg")
                          ) {
                            fileType = "image/jpeg";
                          }
                          // Create a File object with empty content for preview (MessageNode can handle this)
                          return new File([], filename, { type: fileType });
                        })()
                      : headerFile
                  }
                  setHeaderFile={setHeaderFile}
                  body={body}
                  setBody={setBody}
                  sampleBodyFields={sampleBodyFields}
                  setsampleBodyFields={setsampleBodyFields}
                  sampleHeaderFields={sampleHeaderFields}
                  setsampleHeaderFields={setsampleHeaderFields}
                  footer={footer}
                  setFooter={setFooter}
                  templatesNamesData={templatesNamesData}
                  allButtons={allButtons}
                  setAllButtons={setAllButtons}
                  isButtonsValid={isButtonsValid}
                  setIsButtonsValid={setIsButtonsValid}
                  error={error}
                  setError={setError}
                  headerDuplicatePlaceholderError={
                    headerDuplicatePlaceholderError
                  }
                  setHeaderDuplicatePlaceholderError={
                    setHeaderDuplicatePlaceholderError
                  }
                  bodyDuplicatePlaceholderError={bodyDuplicatePlaceholderError}
                  setBodyDuplicatePlaceholderError={
                    setBodyDuplicatePlaceholderError
                  }
                  headerTextError={headerTextError} // NEW
                  setHeaderTextError={setHeaderTextError} // NEW
                  location={location}
                  setLocation={setLocation}
                  locationLatitudeError={locationLatitudeError}
                  locationLongitudeError={locationLongitudeError}
                  setLocationLatitudeError={setLocationLatitudeError}
                  setLocationLongitudeError={setLocationLongitudeError}
                />
              </Box>

              {/* Preview */}
              <MessageNode
                templateName={templateName}
                marketingOption={marketingOption || null}
                language={language}
                headerType={headerType}
                headerText={headerText}
                headerFile={headerFile}
                body={body}
                sampleBodyFields={sampleBodyFields}
                sampleHeaderFields={sampleHeaderFields}
                footer={footer}
                allButtons={allButtons}
                showSampleValues={false} // Use regular preview mode
                location={location}
                mode="edit"
              />
            </Stack>
          )}
        
          {/* Carousel Template */}
          {templateType === "marketing-template" &&
            marketingOption === "carousel" && (
              <Stack
                direction={{ xs: "column", md: "row" }}
                sx={{ flexDirection: { xs: "column", md: "row" } }}
                spacing={2}
              >
                <Box sx={{ flex: 1 }}>
                  <EditCarouselTemplate
                    templateName={templateName}
                    setTemplateName={setTemplateName}
                    language={language}
                    setLanguage={setLanguage}
                    cards={carouselCards}
                    setCards={setCarouselCards}
                    mainBody={carouselMainBody}
                    setMainBody={setCarouselMainBody}
                    mainBodySampleFields={carouselMainBodySampleFields}
                    setMainBodySampleFields={setCarouselMainBodySampleFields}
                    error={error}
                    setError={setError}
                    templatesNamesData={templatesNamesData}
                    carouselType={carouselType}
                    setCarouselType={setCarouselType}
                    carouselMainBodyDuplicatePlaceholderError={
                      carouselMainBodyDuplicatePlaceholderError
                    }
                    setCarouselMainBodyDuplicatePlaceholderError={
                      setCarouselMainBodyDuplicatePlaceholderError
                    }
                    carouselCardBodyDuplicatePlaceholderError={
                      carouselCardBodyDuplicatePlaceholderError
                    }
                    setCarouselCardBodyDuplicatePlaceholderError={
                      setCarouselCardBodyDuplicatePlaceholderError
                    }
                  />
                </Box>

                {/* Preview */}
                <MessageNode
                  templateName={templateName}
                  language={language}
                  headerType="none"
                  headerText=""
                  headerFile={null}
                  body=""
                  sampleBodyFields={[]}
                  sampleHeaderFields={[]}
                  footer=""
                  allButtons={[]}
                  showSampleValues={false}
                  mode="edit"
                  marketingOption="carousel"
                  carouselCards={carouselCards}
                  carouselMainBody={carouselMainBody}
                  carouselMainBodySampleFields={carouselMainBodySampleFields}
                  carouselType={carouselType}
                  {...carouselCards.reduce((acc, card, index) => {
                    if (card.headerFile) {
                      // If headerFile is a string (URL), convert it to a File object and store in cardFile_{index}
                      if (typeof card.headerFile === "string") {
                        console.log("Header file is a URL: ", card.headerFile);
                        // We'll fetch the file synchronously and create a File object placeholder
                        // Since fetch is async, we create a Blob URL for preview, but for upload, you should handle conversion before upload
                        // Here, just create a placeholder File object with the URL as name
                        const url = card.headerFile;
                        const filename =
                          url.split("/").pop() || `cardFile_${index}`;

                        // Dynamically determine file type
                        let fileType = "image/jpeg";
                        if (carouselType === "video" || url.endsWith(".mp4")) {
                          fileType = "video/mp4";
                        } else if (url.endsWith(".png")) {
                          fileType = "image/png";
                        } else if (
                          url.endsWith(".jpg") ||
                          url.endsWith(".jpeg")
                        ) {
                          fileType = "image/jpeg";
                        }
                        try {
                          // This will not actually fetch the file, but will create a File object with the URL as name
                          // For preview, MessageNode can handle both File and string
                          acc[`cardFile_${index}`] = new File([], filename, {
                            type: fileType,
                          }); // or "video/mp4" if needed
                          console.log("Passing as File object");
                        } catch {
                          acc[`cardFile_${index}`] = url;
                        }
                      } else {
                        console.log(
                          "Header file is a File object: ",
                          card.headerFile.name
                        );
                        acc[`cardFile_${index}`] = card.headerFile;
                      }
                    }
                    return acc;
                  }, {})}
                />
              </Stack>
            )}

          {/* Single Product Template */}
          {templateType === "marketing-template" &&
            marketingOption === "single-product" && (
              <Stack
                direction={{ xs: "column", md: "row" }}
                sx={{ flexDirection: { xs: "column", md: "row" } }}
                spacing={2}
              >
                <Box sx={{ flex: 1 }}>
                  <SingleProductTemplateEditor
                    body={body}
                    setBody={setBody}
                    product={spmProduct}
                    setProduct={setSpmProduct}
                    sampleBodyFields={sampleBodyFields}
                    setsampleBodyFields={setsampleBodyFields}
                    footer={footer}
                    setFooter={setFooter}
                    bodyDuplicatePlaceholderError={
                      bodyDuplicatePlaceholderError
                    }
                    setBodyDuplicatePlaceholderError={
                      setBodyDuplicatePlaceholderError
                    }
                  />
                </Box>

                {/* Preview */}
                <MessageNode
                  templateName={templateName}
                  marketingOption="single-product"
                  language={language}
                  headerType="none"
                  headerText=""
                  headerFile={null}
                  body={body}
                  sampleBodyFields={sampleBodyFields}
                  sampleHeaderFields={sampleHeaderFields}
                  footer={footer}
                  showSampleValues={true}
                  location={location}
                  mode="edit"
                  thumbnailImage={spmProduct?.image_url}
                  productName={spmProduct?.name}
                />
              </Stack>
            )}

          {/* Multiple Products Template */}
          {templateType === "marketing-template" &&
            marketingOption === "multiple-products" && (
              <Stack
                direction={{ xs: "column", md: "row" }}
                sx={{ flexDirection: { xs: "column", md: "row" } }}
                spacing={2}
              >
                <Box sx={{ flex: 1 }}>
                  <MultipleProductsTemplateEditor
                    headerType={headerType}
                    setHeaderType={setHeaderType}
                    headerText={headerText}
                    setHeaderText={setHeaderText}
                    body={body}
                    setBody={setBody}
                    sections={mpmSections}
                    setSections={setMpmSections}
                    thumbnail={mpmThumbnail}
                    setThumbnail={setMpmThumbnail}
                    createSection={createMpmSection}
                    createProduct={createMpmProduct}
                    sampleBodyFields={sampleBodyFields}
                    setsampleBodyFields={setsampleBodyFields}
                    sampleHeaderFields={sampleHeaderFields}
                    setsampleHeaderFields={setsampleHeaderFields}
                    footer={footer}
                    setFooter={setFooter}
                    headerDuplicatePlaceholderError={
                      headerDuplicatePlaceholderError
                    }
                    setHeaderDuplicatePlaceholderError={
                      setHeaderDuplicatePlaceholderError
                    }
                    bodyDuplicatePlaceholderError={bodyDuplicatePlaceholderError}
                    setBodyDuplicatePlaceholderError={
                      setBodyDuplicatePlaceholderError
                    }
                    headerTextError={headerTextError}
                    setHeaderTextError={setHeaderTextError}
                  />
                </Box>

                {/* Preview */}
                <MessageNode
                  templateName={templateName}
                  marketingOption="multiple-products"
                  language={language}
                  headerType={headerType}
                  headerText={headerText}
                  headerFile={headerFile}
                  body={body}
                  sampleBodyFields={sampleBodyFields}
                  sampleHeaderFields={sampleHeaderFields}
                  footer={footer}
                  showSampleValues={false}
                  location={location}
                  mode="edit"
                  productCount={mpmSections.reduce(
                    (count, section) => count + section.products.length,
                    0
                  )}
                  thumbnailImage={mpmThumbnail?.image_url}
                />
              </Stack>
            )}
            {allButtons && allButtons.length > 0 && (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={templateTracking}
                      onChange={(e) =>
                        setTemplateTracking(e.target.checked)
                      }
                    />
                  }
                  label="Update Audience Tag with button response"
                  />
              </Box>
            )}
        </Stack>
      </form>
    </div>
  );
};
export default EditTemplate;
